import { Component, Input, forwardRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { NgSelectModule, NgSelectComponent } from '@ng-select/ng-select';
import { NgIf } from '@angular/common';

@Component({
    selector: 'lib-multi-select',
    standalone: true,
    imports: [ NgSelectModule, NgIf, FormsModule ],
    templateUrl: './multi-select.component.html',
    styleUrl: './multi-select.component.scss',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MultiSelectComponent),
        multi: true
    }]
})
export class MultiSelectComponent implements ControlValueAccessor {
    @ViewChild('ngSelect') ngSelect!: NgSelectComponent;

    @Input() items: unknown[] = [];
    @Input() placeholder = 'Select items';
    @Input() width = '250px';
    @Input() bindLabel = 'description';
    @Input() bindValue: string | undefined;

    value: unknown[] = [];
    pendingValue: unknown[] = [];
    disabled = false;

    onChange: (value: unknown) => void = () => {
        // RegisterOnChange will replace placeholder function
    };

    onTouched: () => void = () => {
        // RegisterOnTouched will replace placeholder function
    };

    writeValue(value: unknown[]): void {
        this.value = value || [];
        this.pendingValue = [ ...this.value ];
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onOpen(): void {
        this.pendingValue = [ ...this.value ];
    }

    isSelected(item: unknown): boolean {
        const itemValue = this.getItemValue(item);
        return this.pendingValue.some(v => this.valuesMatch(v, itemValue));
    }

    toggleItem(item: unknown, event: Event): void {
        event.stopPropagation();
        const itemValue = this.getItemValue(item);
        const index = this.pendingValue.findIndex(v => this.valuesMatch(v, itemValue));
        if (index > -1) {
            this.pendingValue = [ ...this.pendingValue.slice(0, index), ...this.pendingValue.slice(index + 1) ];
        } else {
            this.pendingValue = [ ...this.pendingValue, itemValue ];
        }
    }

    apply(event: Event): void {
        event.stopPropagation();
        this.ngSelect.clearModel();
        this.pendingValue.forEach(val => {
            const label = this.bindValue === undefined
                ? this.getItemLabel(val)
                : this.getItemLabel(this.items.find(i => this.getItemValue(i) === val) || val);
            this.ngSelect.select({ label, value: val });
        });
        this.value = [...this.pendingValue];
        this.onChange(this.value);
        this.onTouched();
        this.ngSelect.close();
    }

    clear(event: Event): void {
        event.stopPropagation();
        this.pendingValue = [];
    }

    getSelectedItems(): Record<string, unknown>[] {
        if (this.bindValue === undefined) {
            return this.value as Record<string, unknown>[];
        }
        return this.items.filter(item => {
            const itemValue = this.getItemValue(item);
            return this.value.includes(itemValue);
        }) as Record<string, unknown>[];
    }

    private getItemValue(item: unknown): unknown {
        if (this.bindValue === undefined) {
            return item;
        }
        return (item as Record<string, unknown>)[this.bindValue];
    }

    private getItemLabel(item: unknown): string {
        return String((item as Record<string, unknown>)[this.bindLabel]);
    }

    private valuesMatch(a: unknown, b: unknown): boolean {
        if (this.bindValue === undefined) {
            return this.getItemLabel(a) === this.getItemLabel(b);
        }
        return a === b;
    }
}
